'use client';

import axios from 'axios';
import useSWR from 'swr';
import React, { useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import Button from '@/app/_components/buttons/Button';
import OnboardingSchool from '@/app/_components/onboarding/onboardingSplash/steps/OnboardingSchool';
import OnboardingActivityType from '@/app/_components/onboarding/onboardingSplash/steps/OnboardingActivityType';
import OnboardingYear from '@/app/_components/onboarding/onboardingSplash/steps/OnboardingYear';
import OnboardingMajor from '@/app/_components/onboarding/onboardingSplash/steps/OnboardingMajor';
import OnboardingDone from '@/app/_components/onboarding/onboardingSplash/steps/OnboardingDone';

import { useStepper } from 'headless-stepper';
import { useUserProfile } from '@/app/context/userProfileContext';
import OnboardingLanguage from '@/app/_components/onboarding/onboardingSplash/steps/OnboardingLanguage';
import Navigation from '@/app/_components/onboarding/onboardingSplash/Navigation';

const activityTypesUrl = '/api/taxonomy/activityTypes';

const activityFetcher = () => {
  return axios.get(activityTypesUrl).then(({ data }) => data);
};

interface Props {
  onClose: (language: string) => void;
}

const UserOnboardingForm = (props: Props) => {
  const { onClose } = props;
  const [onboardingType, setOnboardingType] = useState<
    'default' | 'other' | 'done'
  >('default');

  const { data: activityTypes } = useSWR(activityTypesUrl, activityFetcher);
  const { userProfile } = useUserProfile();

  const methods = useForm({
    mode: 'all',
    ...(userProfile?.user_preferences.preferred_language
      ? {
          defaultValues: {
            language: userProfile?.user_preferences.preferred_language,
          },
        }
      : {}),
  });

  const defaultSteps = React.useMemo(
    () => [
      {
        label: 'SCHOOL',
        hasNext: true,
        hasPrev: false,
        hasSubmit: false,
      },
      {
        label: 'MAJOR',
        hasNext: true,
        hasSubmit: false,
        hasPrev: true,
      },
      {
        label: 'YEAR',
        hasNext: !userProfile?.user_flags.preferred_language_informed,
        hasSubmit: userProfile?.user_flags.preferred_language_informed,
        hasPrev: true,
      },

      ...(!userProfile?.user_flags.preferred_language_informed
        ? [
            {
              label: 'LANGUAGE',
              hasNext: false,
              hasSubmit: true,
              hasPrev: true,
            },
          ]
        : []),
    ],
    [userProfile]
  );

  const otherSteps = React.useMemo(
    () => [
      {
        label: 'ACTIVITY_TYPE',
        hasNext: !userProfile?.user_flags.preferred_language_informed,
        hasSubmit: userProfile?.user_flags.preferred_language_informed,
        hasPrev: true,
      },
      ...(!userProfile?.user_flags.preferred_language_informed
        ? [
            {
              label: 'LANGUAGE',
              hasNext: false,
              hasSubmit: true,
              hasPrev: true,
            },
          ]
        : []),
    ],
    []
  );

  const {
    state: defaultState,
    nextStep: defaultNextStep,
    prevStep: defaultPrevStep,
  } = useStepper({ steps: defaultSteps });

  const {
    state: otherState,
    nextStep: otherNextStep,
    prevStep: otherPrevStep,
  } = useStepper({ steps: otherSteps });

  const onFormSubmit = (data: any) => {
    setOnboardingType('done');
    onClose(data);
  };

  const preventEnterKeySubmission = (
    e: React.KeyboardEvent<HTMLFormElement>
  ) => {
    const target = e.target;
    if (e.key === 'Enter' && target instanceof HTMLInputElement) {
      e.preventDefault();
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        className="flex min-h-screen mb-10 md:mb-0 p-10 justify-center"
        onSubmit={methods.handleSubmit(onFormSubmit)}
        onKeyDown={preventEnterKeySubmission}
      >
        <div className="flex flex-col items-center">
          {onboardingType === 'default' && (
            <Navigation
              steps={defaultSteps}
              onBack={defaultPrevStep}
              state={defaultState}
            />
          )}
          {onboardingType === 'other' && (
            <Navigation
              steps={otherSteps}
              onBack={() => {
                if (
                  otherSteps[otherState.currentStep].label === 'ACTIVITY_TYPE'
                ) {
                  methods.reset();
                  setOnboardingType('default');
                } else {
                  otherPrevStep();
                }
              }}
              state={otherState}
            />
          )}
          <div className="pt-8 flex flex-col flex-grow justify-center">
            <div className="mb-8">
              <svg
                width="73"
                height="105"
                viewBox="0 0 73 105"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M29.1499,35.1917c-1.7896.7491-.0208,15.2534-.0208,15.2534.7284.8116,5.0984-7.0544,5.0984-7.0544s8.3238.7491,9.1354.0208c.7907-.7283-1.3526-15.7321-2.1434-16.3772s-4.9527,8.0117-4.9527,8.0117-5.3064-.5826-7.1169.1457ZM72.1842,75.0212c-.8948-1.2485-7.0753-3.7665-7.7204-3.3919-4.037-11.2788-15.2534-19.3321-28.4051-19.2489s-24.2432,8.303-28.15536,19.6026c-.95724-.1664-6.74231,2.2891-7.595504,3.5169-.894813,1.2902.312144,12.8811,1.685574,13.9216c1.23049.9375,5.28409.7018,6.1387.6521.05593-.0033.09815-.0057.12499-.007c4.3076,9.7181,15.7112,14.5044,28.6756,14.4004c12.9644-.083,23.6397-5.0153,27.5311-14.7958.6243.0416,4.9527.2497,6.2221-.7283c1.3526-1.0405,2.4139-12.6523,1.4983-13.9217ZM61.5713,80.8271c.0832,13.5471-10.8834,20.3519-25.0548,20.4559-14.1505.104-26.1368-6.5551-26.2201-20.1021-.0832-13.5262,11.3205-24.5969,25.471-24.6802c14.1713-.104,25.7206,10.7794,25.8039,24.3264Zm-25.7831-21.85c-13.2765.104-23.9726,10.4672-23.8894,23.161.0832,12.6939,11.3204,18.9369,24.5969,18.8539c13.2766-.104,23.5773-6.493,23.4941-19.1868-.1041-12.6939-10.9251-22.9114-24.2016-22.8281Z"
                  clip-rule="evenodd"
                  fill="#fff"
                  fill-rule="evenodd"
                />
                <g id="open-eyes" opacity="0">
                  <path
                    d="M32.9944,90.1759c1.4504.0652,2.8757.0602,4.3211-.1205c1.4203-.1756,2.8607-.4466,4.1556-1.079.3061-.1506.286-.7327-.1305-.6575-1.3902.246-2.7553.6324-4.1505.8482-1.3952.2208-2.8306.3513-4.2409.3112-.4517-.0151-.3964.6725.0452.6925v.0051Z"
                    clip-rule="evenodd"
                    fill="#8a27ed"
                    fill-rule="evenodd"
                  />
                  <path
                    d="M27.9948,79.2813C27.9875,82.1974,25.9729,83.0009,23.487,83s-4.495-.8112-4.487-3.7239c.0074-2.9161,2.0285-5.277,4.5144-5.2761c2.486.0009,4.4929,2.3647,4.4856,5.2808l-.0052.0005Z"
                    clip-rule="evenodd"
                    fill="#8a27ed"
                    fill-rule="evenodd"
                  />
                  <path
                    d="M53.9943,79.2813c-.0082,2.9161-2.2466,3.7196-5.0087,3.7187s-4.9945-.8113-4.9856-3.7239c.0082-2.9161,2.2539-5.277,5.0161-5.2761c2.7621.0009,4.992,2.3647,4.9839,5.2808l-.0057.0005Z"
                    clip-rule="evenodd"
                    fill="#8a27ed"
                    fill-rule="evenodd"
                  />
                </g>
                <g id="closed-eyes" transform="translate(.000001 0)">
                  <path
                    d="M32.2068,88.3974c.4116-.0809,7.888-.5258,8.5844-.3611s-.2446,3.6653-4.4223,3.9484-4.7819-3.4643-4.1621-3.5873Z"
                    clip-rule="evenodd"
                    fill="#8a27ed"
                    fill-rule="evenodd"
                  />
                  <path
                    d="M27.9933,79.5231c-.0077,4.5404-2.1282,5.7914-4.7449,5.7901s-4.7315-1.2631-4.723-5.7981c.0078-4.5403,2.1352-8.2161,4.7519-8.2148c2.6166.0013,4.7292,3.6818,4.7214,8.2221l-.0054.0007Z"
                    clip-rule="evenodd"
                    fill="#8a27ed"
                    fill-rule="evenodd"
                  />
                  <path
                    d="M53.6705,79.5503c-.0077,4.5403-2.1282,5.7913-4.7448,5.79-2.6167-.0014-4.7315-1.2631-4.723-5.7981.0077-4.5403,2.1352-8.2161,4.7518-8.2148s4.7292,3.6818,4.7215,8.2221l-.0055.0008Z"
                    clip-rule="evenodd"
                    fill="#8a27ed"
                    fill-rule="evenodd"
                  />
                </g>
                <path
                  d="M66.5988,18.0756c-.1568.5321-.574.7924-1.0177,1.0011-1.2993.63-2.6269,1.2244-3.928,1.8704-2.1725,1.0653-4.3309,2.1483-6.4893,3.2313-.3249.1575-.6515.331-.9745.4725-.355.138-.7452.1588-.9825-.1751-.3063-.4386-.0539-.8144.2994-1.0818.3871-.2798.8043-.5401,1.232-.7507c3.3689-1.6421,6.7378-3.2843,10.1049-4.9105.3248-.1575.6674-.3291,1.0116-.3713.5074-.0561.782.2334.7405.7461l.0036-.032Z"
                  fill="#952dfe"
                />
                <path
                  d="M42.8631,0.37435c.3922.392232.4211.8831.3922,1.37264-.0685,1.44232-.1818,2.89253-.2371,4.34405-.1041,2.41746-.1858,4.83096-.2675,7.24446-.0171.3606-.021.7304-.0513,1.0818-.0487.3776-.2172.7303-.6239.7789-.5316.0592-.7409-.3423-.8067-.7805-.0605-.4738-.0894-.9646-.0697-1.441.17-3.74398.3399-7.48794.523-11.2227.0171-.36058.0303-.74354.1579-1.065933.1935-.472408.5791-.574984,1.0095-.29329l-.0264-.018427Z"
                  fill="#952dfe"
                />
                <path
                  d="M16.635,11.8759c.5537-.0332.9402.2707,1.2873.6172c1.0338,1.0081,2.0438,2.0548,3.0932,3.0592c1.7399,1.6816,3.4916,3.3438,5.2434,5.0061.2585.252.5325.5003.7754.756.2503.287.4024.647.169.9836-.3085.4371-.7477.3273-1.1192.0858-.3947-.269-.7812-.573-1.1245-.9038-2.6888-2.6109-5.3776-5.2217-8.0507-7.8363-.2585-.2521-.5363-.516-.6928-.8254-.2252-.4582-.0461-.8147.4502-.9499l-.0313.0075Z"
                  fill="#952dfe"
                />
              </svg>
            </div>
            {onboardingType === 'default' && (
              <>
                <OnboardingSchool
                  step={defaultSteps[defaultState.currentStep].label}
                  hasNext={defaultSteps[defaultState.currentStep].hasNext}
                  onNext={defaultNextStep}
                />
                <OnboardingMajor
                  step={defaultSteps[defaultState.currentStep].label}
                  hasNext={defaultSteps[defaultState.currentStep].hasNext}
                  onNext={defaultNextStep}
                />
                <OnboardingYear
                  step={defaultSteps[defaultState.currentStep].label}
                  hasSubmit={defaultSteps[defaultState.currentStep].hasSubmit}
                  hasNext={defaultSteps[defaultState.currentStep].hasNext}
                  onNext={defaultNextStep}
                />
                <OnboardingLanguage
                  step={defaultSteps[defaultState.currentStep].label}
                />
                <div>
                  {defaultState.currentStep === 0 && (
                    <Button
                      data-test-id="not-a-student-option"
                      className="text-purple3"
                      variant="tertiary"
                      type="button"
                      label={`I’m not a student`}
                      onClick={() => {
                        setOnboardingType('other');
                        methods.reset();
                      }}
                    />
                  )}
                </div>
              </>
            )}
            {onboardingType === 'other' && (
              <>
                <OnboardingActivityType
                  step={otherSteps[otherState.currentStep].label}
                  data={activityTypes}
                  hasSubmit={otherSteps[otherState.currentStep].hasSubmit}
                  hasNext={otherSteps[otherState.currentStep].hasNext}
                  onNext={otherNextStep}
                />
                <OnboardingLanguage
                  step={otherSteps[otherState.currentStep].label}
                />
              </>
            )}
            {onboardingType === 'done' && <OnboardingDone />}
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default UserOnboardingForm;
